import type { WizardStepProps } from '../../../../types/builder.types'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'

export function DataSourceStep({ config, onChange, onNext }: WizardStepProps) {
  const { dataSources, loading, error } = useDataSources()

  const handleSelect = (tableName: string) => {
    onChange({ ...config, dataSource: tableName })
  }

  // Separar fuentes ETL de fuentes CKAN
  const { etlSources, ckanSources } = useMemo(() => {
    const etl = dataSources.filter(ds => !ds.tableName.startsWith('ckan_data_generic'))
    const ckan = dataSources.filter(ds => ds.tableName.startsWith('ckan_data_generic'))
    return { etlSources: etl, ckanSources: ckan }
  }, [dataSources])

  return (
    <div className="max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold mb-2">Paso 1: Selecciona la Fuente de Datos</h2>
      <p className="text-gray-600 mb-6">
        Elige la tabla de donde se obtendrán los datos para tu componente
      </p>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <p className="text-gray-600 mt-4">Cargando fuentes de datos...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800">
            <strong>⚠️ Advertencia:</strong> {error}
          </p>
          <p className="text-sm text-yellow-700 mt-2">
            Mostrando solo fuentes de datos del ETL. Las fuentes de CKAN no están disponibles.
          </p>
        </div>
      )}

      {/* Tablas ETL */}
      {!loading && etlSources.length > 0 && (
        <>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-600 rounded-full"></span>
              Fuentes de Datos del ETL
              <span className="text-sm font-normal text-gray-500">({etlSources.length} disponibles)</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Datos procesados mediante el sistema ETL de archivos CSV, Excel y Shapefiles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {etlSources.map((source) => (
              <button
                key={source.tableName}
                onClick={() => handleSelect(source.tableName)}
                className={`
                  text-left border-2 rounded-lg p-4 transition-all hover:shadow-md
                  ${
                    config.dataSource === source.tableName
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="text-3xl">{source.icon}</span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{source.label}</h3>
                    <p className="text-sm text-gray-500">{source.recordCount.toLocaleString('es-ES')} registros</p>
                  </div>
                  {config.dataSource === source.tableName && (
                    <span className="text-green-600 text-xl">✓</span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{source.description}</p>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Opción de Custom Query */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
          Consulta Personalizada
        </h3>
        <button
          onClick={() => handleSelect('custom_query')}
          className={`
            w-full text-left border-2 rounded-lg p-4 transition-all hover:shadow-md
            ${
              config.dataSource === 'custom_query'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }
          `}
        >
          <div className="flex items-start gap-3">
            <span className="text-3xl">⚙️</span>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Consulta SQL Personalizada</h3>
              <p className="text-sm text-gray-600 mt-1">
                Escribe tu propia consulta SQL para obtener datos específicos.
                Útil para joins, agregaciones complejas o filtros avanzados.
              </p>
              <p className="text-xs text-blue-600 mt-2">
                💡 Podrás configurar la consulta SQL en el paso de configuración
              </p>
            </div>
            {config.dataSource === 'custom_query' && (
              <span className="text-blue-600 text-xl">✓</span>
            )}
          </div>
        </button>
      </div>

      {/* Tablas CKAN */}
      {!loading && ckanSources.length > 0 && (
        <>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
              Recursos de CKAN
              <span className="text-sm font-normal text-gray-500">({ckanSources.length} disponibles)</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Datasets sincronizados desde el catálogo CKAN de datos abiertos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {ckanSources.map((source) => (
              <button
                key={source.tableName}
                onClick={() => handleSelect(source.tableName)}
                className={`
                  text-left border-2 rounded-lg p-4 transition-all hover:shadow-md
                  ${
                    config.dataSource === source.tableName
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }
                `}
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="text-3xl">{source.icon}</span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{source.label}</h3>
                    <p className="text-sm text-gray-500">{source.recordCount.toLocaleString('es-ES')} registros</p>
                  </div>
                  {config.dataSource === source.tableName && (
                    <span className="text-purple-600 text-xl">✓</span>
                  )}
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">{source.description}</p>
              </button>
            ))}
          </div>
        </>
      )}

      <button
        onClick={onNext}
        disabled={!config.dataSource || loading}
        className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        Continuar →
      </button>
    </div>
  )
}
